import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileBarChart, Sparkles, Loader2, Target, DollarSign, TrendingUp,
  CheckCircle2, AlertTriangle, Lightbulb, BookOpen, ChevronRight,
  Download, Trash2, Eye, Calendar, RefreshCw, BarChart3, PieChart,
  ArrowUpRight, ArrowDownRight, Clock
} from 'lucide-react';
import { campaignsAPI, reportBuilderAPI } from '../api';

const ScoreGauge = ({ score, label, color = 'primary' }) => {
  const getColor = () => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-slate-200"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={251.2}
            strokeDashoffset={251.2 - (251.2 * score) / 100}
            className={getColor()}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-bold ${getColor()}`}>{score}%</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-medium text-muted-foreground">{label}</span>
    </div>
  );
};

export const AIReportBuilderPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');

  useEffect(() => {
    fetchCampaigns();
    fetchGeneratedReports();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await campaignsAPI.getAll({ limit: 100 });
      setCampaigns(res.data?.campaigns || res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneratedReports = async () => {
    try {
      const res = await reportBuilderAPI.getGeneratedReports({ limit: 50 });
      setGeneratedReports(res.data?.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleGenerateReport = async () => {
    if (selectedCampaigns.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    try {
      setGenerating(true);
      const res = await reportBuilderAPI.generateReport({
        campaign_ids: selectedCampaigns,
        report_type: 'comprehensive',
        include_recommendations: true,
        include_learnings: true
      });

      toast.success('Report generated successfully!');
      setCurrentReport(res.data);
      setShowReportDialog(true);
      setSelectedCampaigns([]);
      fetchGeneratedReports();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      const res = await reportBuilderAPI.getReport(reportId);
      setCurrentReport(res.data);
      setShowReportDialog(true);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    
    try {
      await reportBuilderAPI.deleteReport(reportId);
      toast.success('Report deleted');
      fetchGeneratedReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-report-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileBarChart className="h-6 w-6" />
            AI Report Builder
          </h1>
          <p className="text-muted-foreground">
            Generate AI-powered campaign analysis reports with goals, ROI, and recommendations
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchCampaigns(); fetchGeneratedReports(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generate">Generate Report</TabsTrigger>
          <TabsTrigger value="history">Report History ({generatedReports.length})</TabsTrigger>
        </TabsList>

        {/* Generate Report Tab */}
        <TabsContent value="generate" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Campaign Selection */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Select Campaigns
                </CardTitle>
                <CardDescription>
                  Choose one or more campaigns to analyze
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {campaigns.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No campaigns available
                      </p>
                    ) : (
                      campaigns.map(campaign => (
                        <div
                          key={campaign.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedCampaigns.includes(campaign.id)
                              ? 'border-primary bg-primary/5'
                              : 'hover:border-slate-300'
                          }`}
                          onClick={() => toggleCampaign(campaign.id)}
                          data-testid={`campaign-select-${campaign.id}`}
                        >
                          <Checkbox
                            checked={selectedCampaigns.includes(campaign.id)}
                            onCheckedChange={() => toggleCampaign(campaign.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{campaign.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {campaign.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {campaign.campaign_type?.replace('_', ' ')} • 
                              Budget: ${campaign.budget?.toLocaleString() || 0}
                            </p>
                          </div>
                          {selectedCampaigns.includes(campaign.id) && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Generate Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Report Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <h4 className="font-medium mb-2">Report Includes:</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Overall Completion Score
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Goals vs Actual Analysis
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Budget & ROI Analysis
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      AI Recommendations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Learnings & Improvements
                    </li>
                  </ul>
                </div>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {selectedCampaigns.length} campaign(s) selected
                  </p>
                  <Button
                    onClick={handleGenerateReport}
                    disabled={generating || selectedCampaigns.length === 0}
                    className="w-full"
                    size="lg"
                    data-testid="generate-report-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate AI Report
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {generatedReports.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">No reports generated yet</p>
                <Button variant="outline" onClick={() => setActiveTab('generate')}>
                  Generate Your First Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {generatedReports.map(report => (
                <Card key={report.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                          <FileBarChart className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{report.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {report.campaign_names?.length || 0} campaign(s) • 
                            Score: {report.overall_completion_score}%
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            {new Date(report.generated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewReport(report.id)}
                          data-testid={`view-report-${report.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteReport(report.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Report View Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5" />
              {currentReport?.title || 'Campaign Report'}
            </DialogTitle>
            <DialogDescription>
              Generated {currentReport?.generated_at && new Date(currentReport.generated_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {currentReport && (
            <div className="space-y-6 py-4">
              {/* Score Cards */}
              <div className="flex justify-center gap-8 py-4 bg-slate-50 rounded-lg">
                <ScoreGauge score={currentReport.overall_completion_score || 0} label="Overall Score" />
                <ScoreGauge score={currentReport.goal_achievement_score || 0} label="Goal Achievement" />
                <ScoreGauge score={currentReport.budget_efficiency_score || 0} label="Budget Efficiency" />
                <ScoreGauge score={currentReport.roi_score || 0} label="ROI Score" />
              </div>

              {/* Executive Summary */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                  <BarChart3 className="h-4 w-4" />
                  Executive Summary
                </h3>
                <p className="text-sm">{currentReport.executive_summary}</p>
              </div>

              {/* Key Highlights */}
              {currentReport.key_highlights?.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4" />
                    Key Highlights
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {currentReport.key_highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Goals Analysis */}
              {currentReport.goals_analysis && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Target className="h-4 w-4" />
                    Goals Analysis
                  </h3>
                  <div className="space-y-3">
                    {currentReport.goals_analysis.goals_met?.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-sm font-medium text-green-800 mb-2">Goals Met</p>
                        <ul className="space-y-1">
                          {currentReport.goals_analysis.goals_met.map((goal, idx) => (
                            <li key={idx} className="text-sm flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {currentReport.goals_analysis.goals_missed?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-sm font-medium text-red-800 mb-2">Goals Missed</p>
                        <ul className="space-y-1">
                          {currentReport.goals_analysis.goals_missed.map((goal, idx) => (
                            <li key={idx} className="text-sm flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">{currentReport.goals_analysis.analysis}</p>
                  </div>
                </div>
              )}

              {/* Budget Analysis */}
              {currentReport.budget_analysis && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <DollarSign className="h-4 w-4" />
                    Budget Analysis
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Total Budget</p>
                      <p className="text-xl font-bold">${(currentReport.budget_analysis.total_budget || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Total Spent</p>
                      <p className="text-xl font-bold">${(currentReport.budget_analysis.total_spent || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Utilization</p>
                      <p className="text-xl font-bold">{currentReport.budget_analysis.utilization_rate || 0}%</p>
                    </div>
                  </div>
                  <Badge className={
                    currentReport.budget_analysis.efficiency_rating === 'Good' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }>
                    {currentReport.budget_analysis.efficiency_rating}
                  </Badge>
                  <p className="text-sm text-muted-foreground mt-2">{currentReport.budget_analysis.analysis}</p>
                </div>
              )}

              {/* ROI Analysis */}
              {currentReport.roi_analysis && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4" />
                    ROI Analysis
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Expected ROI</p>
                      <p className="text-xl font-bold">{currentReport.roi_analysis.expected_roi || 0}%</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Actual ROI</p>
                      <p className="text-xl font-bold flex items-center justify-center gap-1">
                        {currentReport.roi_analysis.actual_roi || 0}%
                        {(currentReport.roi_analysis.roi_variance || 0) > 0 ? (
                          <ArrowUpRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-600" />
                        )}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Variance</p>
                      <p className={`text-xl font-bold ${(currentReport.roi_analysis.roi_variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(currentReport.roi_analysis.roi_variance || 0) >= 0 ? '+' : ''}{currentReport.roi_analysis.roi_variance || 0}%
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{currentReport.roi_analysis.analysis}</p>
                </div>
              )}

              <Separator />

              {/* Recommendations */}
              {currentReport.recommendations?.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <Lightbulb className="h-4 w-4" />
                    Recommendations for Next Campaign
                  </h3>
                  <div className="space-y-3">
                    {currentReport.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getPriorityColor(rec.priority)}>
                            {rec.priority}
                          </Badge>
                          <span className="font-medium">{rec.title}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{rec.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Learnings */}
              {currentReport.learnings?.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <BookOpen className="h-4 w-4" />
                    Key Learnings
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {currentReport.learnings.map((learning, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 rounded-lg">
                        <Badge variant="outline" className="mb-2">{learning.category}</Badge>
                        <p className="text-sm font-medium">{learning.insight}</p>
                        <p className="text-xs text-muted-foreground mt-1">{learning.evidence}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Improvements */}
              {currentReport.improvements?.length > 0 && (
                <div>
                  <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <ChevronRight className="h-4 w-4" />
                    Areas for Improvement
                  </h3>
                  <div className="space-y-2">
                    {currentReport.improvements.map((imp, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-amber-700">{idx + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium">{imp.area}</p>
                          <p className="text-sm text-muted-foreground">{imp.suggestion}</p>
                          <p className="text-xs text-green-600 mt-1">Expected Impact: {imp.expected_impact}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIReportBuilderPage;
